// w33 Tog' Sahari — variant that renders the standalone HTML at
// /templates/mountain/index.html (mobile-first watercolor wedding
// invitation: a 3-layer parallax mountain hero — distant range, green
// hills, foreground florals — cream + sage + gold, Cormorant Garamond
// + Pinyon Script; ornamental-frame invitation, ceremony + celebration
// scenes, programme timeline, rotating mandala + live countdown,
// wax-seal footer) inside a full-bleed iframe.
//
// The HTML's customizer accepts DD.MM.YYYY directly — names/date/time/
// venue/address pass through raw on the query string.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

// Flat .html — NOT a nested index.html. Go's http.FileServer 301-redirects
// any request ending in /index.html to the bare directory, which then falls
// through to the SPA and renders the wrong template. Every static template
// in this repo is a flat file for exactly this reason.
const TEMPLATE_HTML = "/templates/mountain.html";

export function MountainStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) params.set("date", t.date);
  if (t.time) params.set("time", t.time);
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  // ?map_url= drives the "Tashrifni tasdiqlash" button (opens the venue
  // in maps); when absent the button scrolls to the wishes section.
  if (t.mapUrl) params.set("map_url", t.mapUrl);
  // Schedule rides as a JSON array of {time,title,description}. Forwarded
  // only when the owner has touched the field (array present, even empty)
  // so the customizer can tell "owner cleared it" (hide) from "never
  // edited" (hidden on a real invite; sample shown only in demo).
  const schedule = Array.isArray(t.custom?.schedule) ? t.custom.schedule : null;
  if (schedule !== null) params.set("schedule", JSON.stringify(schedule));
  // Editable ceremony / reception cards. Forwarded even when blank so the
  // customizer can tell "owner cleared it" (hide the card) from "never
  // edited" (undefined → keep the template's default card).
  const cerTitle = t.custom?.ceremony_title;
  if (typeof cerTitle === "string") params.set("ceremony_title", cerTitle);
  const cerText = t.custom?.ceremony_text;
  if (typeof cerText === "string") params.set("ceremony_text", cerText);
  const celTitle = t.custom?.celebration_title;
  if (typeof celTitle === "string") params.set("celebration_title", celTitle);
  const celText = t.custom?.celebration_text;
  if (typeof celText === "string") params.set("celebration_text", celText);
  // Per-card time override; blank/absent → the card falls back to the
  // main ?time= (step 1) so both cards default to the couple's time.
  const ceremonyTime = t.custom?.ceremony_time;
  if (typeof ceremonyTime === "string") params.set("ceremony_time", ceremonyTime);
  const celebrationTime = t.custom?.celebration_time;
  if (typeof celebrationTime === "string") params.set("celebration_time", celebrationTime);
  if (ctx.slug) params.set("slug", ctx.slug);
  // ?audio=/music/<id>.mp3 — owner-picked soundtrack from the
  // curated MusicSelector library. Empty/unset = silent card.
  if (t.musicTrackId) params.set("audio", "/music/" + t.musicTrackId + ".mp3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Tog' Sahari"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#f1e3bf",
      }}
    />
  );
}
